import type { ReactNode } from "react";
import { Box, RowState, Stack, Typography } from "@app/ui-kit";

/**
 * The selected question's detail, beside its table (2026-10-14, stage two of the row rule). Guidance is not a child of
 * the question, it is detail about it, so it does not sit behind a chevron: one row is selected at a time, the panel
 * follows it, and Arrow Up and Arrow Down walk the table so reading five guidance notes is four keystrokes. The panel
 * is a labelled region with its own heading; with nothing selected it says so in a sentence; a question with no
 * guidance says "No guidance yet" under the heading, never an empty section. The one exception is the engagement's
 * question list, where guidance is the job and sits under every question, always visible, with no panel.
 */
export type QuestionDetail = {
  text: string;
  guidance: string | null;
  lens: string;
  questionType: string;
  evidenceExpected: string | null;
  code: string;
  /** Where it is used: "No engagement yet", "2 engagements", "3 clients". */
  used: string;
  /** Whose it is, where two owners share the list ("From the library", "Yours"). */
  origin?: string | null;
  /** The row's state, with its date, whenever it has one: the panel carries everything about the selected row. */
  state?: { label: string; acting?: boolean } | null;
  /** Parts (2026-10-19): how many parts the question has. With parts and no guidance of its own the guidance block is omitted, not replaced: the parts sit below and each carries its own. */
  partCount?: number;
};

const Row = ({ label, children, mono = false }: { label: string; children: ReactNode; mono?: boolean }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.6 }}>{label}</Typography>
    <Typography variant="body2" sx={mono ? { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 } : undefined}>{children}</Typography>
  </Box>
);

export function QuestionDetailPanel({ question, actions, parts, emptyText = "Select a question to read its guidance and details here.", id = "question-detail" }: { question: QuestionDetail | null; actions?: ReactNode; /** Parts (2026-10-18): the question's parts, in the panel and never behind a chevron. */ parts?: ReactNode; emptyText?: string; id?: string }) {
  const headingId = `${id}-heading`;
  return (
    <Box component="section" role="region" aria-labelledby={headingId} data-testid="question-detail" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, alignSelf: "start", position: { lg: "sticky" }, top: { lg: 16 } }}>
      <Typography id={headingId} variant="subtitle2" sx={{ mb: 1.5 }}>{question ? "The selected question" : "Question detail"}</Typography>
      {!question ? (
        <Typography variant="body2" color="text.secondary" data-testid="question-detail-empty">{emptyText}</Typography>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body1" data-testid="detail-text">{question.text}</Typography>
          {question.state && <Typography variant="body2" data-testid="detail-state"><RowState label={question.state.label} acting={question.state.acting} /></Typography>}
          {/* A question with no parts and no guidance says so: that is a real gap. One whose parts carry the guidance shows no block at all. */}
          {(question.guidance || !(question.partCount ?? 0)) && (
            <Box>
              <Typography variant="subtitle2" component="h3" sx={{ fontSize: 13 }}>Guidance for the interviewer</Typography>
              <Typography variant="body2" color={question.guidance ? "text.primary" : "text.secondary"} data-testid="detail-guidance" sx={{ whiteSpace: "pre-line" }}>{question.guidance ?? "No guidance yet."}</Typography>
            </Box>
          )}
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Row label="Lens">{question.lens}</Row>
            <Row label="Answer">{question.questionType}</Row>
            {question.origin && <Row label="Origin">{question.origin}</Row>}
          </Stack>
          <Row label="Evidence expected">{question.evidenceExpected ?? "Not stated."}</Row>
          {parts}
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Row label="Code" mono>{question.code}</Row>
            <Row label="Used">{question.used}</Row>
          </Stack>
          {actions && <Box sx={{ pt: 0.5, borderTop: 1, borderColor: "divider" }} data-testid="detail-actions">{actions}</Box>}
        </Stack>
      )}
    </Box>
  );
}

/**
 * The table and its panel: side by side where the viewport carries both, the panel beneath the table where it does not.
 * `sideFrom` is the breakpoint from which they sit side by side: a wide table (the bank's seven columns) needs more
 * room than a short one, and beneath it the table scrolls inside its own box so the panel stays in view.
 */
export const TableWithPanel = ({ table, panel, sideFrom = "lg" }: { table: ReactNode; panel: ReactNode; sideFrom?: "lg" | "xl" }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", [sideFrom]: "minmax(0, 1fr) 340px" }, gap: 2, alignItems: "start" }}>
    <Box sx={{ minWidth: 0 }}>{table}</Box>
    {panel}
  </Box>
);
